import React from "react";

import { useRouter } from "next/router";
import styled from "styled-components";

import { VaspDueDiligenceReviewStatus } from "../../types";
import VaspDueDiligenceReviewPanel from "../VaspDueDiligenceReviewPanel/VaspDueDiligenceReviewPanel";
import BlockedVaspLink from "./components/BlockedVaspLink";
import MonitoredVaspLink from "./components/MonitoredVaspLink";
import ReviewVaspLink from "./components/ReviewVaspLink";
import TrustedVaspLink from "./components/TrustedVaspLink";

import SidePanel from "~/components/SidePanel/SidePanel";
import Spacer, { Axis } from "~/components/Spacer";
import { ViewerPermissions } from "~/contexts/ViewerContext";
import useCurrentViewerPermissions from "~/hooks/useCurrentViewerPermissions";
import { removeQueryParam } from "~/utils/url";

import "react-sliding-side-panel/lib/index.css";

const ContainerBox = styled.section`
  width: 400px;
  height: 73px;
  padding: 0px 16px 0 20px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  /* Black 6 */
  background: #e9e9e9;
  border-radius: 8px;
`;

const DueDiligenceText = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  text-transform: capitalize;
  color: #1f1f1f;
`;

const StatusTextText = styled.div`
  font-weight: 700;
  font-size: 10px;
  line-height: 12px;
  color: #000000;
`;

type VASPDueDiligenceReviewSummaryProps = {
  reviewedVaspDID: string;
  vaspReviewStatus: VaspDueDiligenceReviewStatus | null;
  vaspReviewIncludeInWidget?: string | null;
  vaspReviewNote?: string | null;
  lastReviewedDate?: Date | null;
  lastReviewedBy?: string | null;
  onVaspDataUpdated: () => void;
};

export const formatDate = (date: Date) => {
  // i.e LAST REVIEWED AUG 2 , 2023
  return `Last reviewed ${date.toLocaleString("default", {
    month: "short",
  })} ${date.getDate()} , ${date.getFullYear()} `;
};

export default function VASPDueDiligenceReviewSummary({
  reviewedVaspDID,
  vaspReviewStatus,
  vaspReviewIncludeInWidget,
  vaspReviewNote,
  lastReviewedDate,
  lastReviewedBy,
  onVaspDataUpdated,
}: VASPDueDiligenceReviewSummaryProps) {
  const router = useRouter();
  const viewerPermissions = useCurrentViewerPermissions();
  const { query } = router;
  const openVaspReviewSidePanel: boolean | null =
    query && query.open_vasp_review ? Boolean(query.open_vasp_review as string) : null;

  const openDueDiligenceSidePanel = () => {
    router.replace({ pathname: location.pathname, query: { open_vasp_review: true } });
  };

  const closeDueDiligenceSidePanel = () => {
    removeQueryParam("open_vasp_review", router);
  };

  const canChangeTrust = viewerPermissions.includes(ViewerPermissions["rules.set"]);

  return (
    <>
      <ContainerBox>
        <div>
          <DueDiligenceText>DUE DILIGENCE</DueDiligenceText>
          <Spacer axis={Axis.VERTICAL} size={4} />
          <StatusTextText>{lastReviewedDate ? formatDate(lastReviewedDate) : "Not completed"}</StatusTextText>
        </div>

        <div onClick={canChangeTrust ? openDueDiligenceSidePanel : () => null}>
          {vaspReviewStatus === null && <ReviewVaspLink hasPadding clickable={canChangeTrust} />}
          {vaspReviewStatus === "TRUSTED" && <TrustedVaspLink hasPadding clickable={canChangeTrust} />}
          {vaspReviewStatus === "BLOCKED" && <BlockedVaspLink hasPadding clickable={canChangeTrust} />}
          {vaspReviewStatus === "MANUAL" && <MonitoredVaspLink hasPadding clickable={canChangeTrust} />}
        </div>
      </ContainerBox>
      <SidePanel
        isOpen={openVaspReviewSidePanel === true}
        onClose={closeDueDiligenceSidePanel}
        backdropClicked={closeDueDiligenceSidePanel}
      >
        <VaspDueDiligenceReviewPanel
          reviewedVaspDID={reviewedVaspDID}
          vaspReviewStatus={vaspReviewStatus}
          vaspReviewIncludeInWidget={vaspReviewIncludeInWidget}
          vaspReviewNote={vaspReviewNote}
          lastReviewedDate={lastReviewedDate}
          lastReviewedBy={lastReviewedBy}
          onVaspDataUpdated={onVaspDataUpdated}
        />
      </SidePanel>
    </>
  );
}
